import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import placesAPI from "../../../api/placesAPI";
import swalDialog from "../../../helpers/swalErrorPopup";
import { isLoadChanger } from "../../../store/mainSlice";
import { selectPartner } from "../../../store/partnerStore";
import { isV2Enabled, V2_SCREEN_KEYS } from "../../registry";
import { commitClientLocation, formatAddress } from "../../utils/address";
import {
  GPS_FLOW_ENABLED,
  hasKnownGpsDenial,
  requestLocation,
  resolveLocationIfGranted,
  watchGeolocationPermission,
} from "../../utils/resolveLocation";
import ScreenHeader from "../../components/ScreenHeader";
import "./addressSelect.scss";

// V2 address-selection page ("בחירת כתובת"). Routed at /address-select, no V1
// counterpart. Google Places autocomplete (Israel, Hebrew); picking a suggestion
// saves the address to the backend (UpdateClientLocation) and to userSlice.

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const PinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 21.5c-3.4-3.2-7-7.2-7-11A7 7 0 0 1 12 3.5a7 7 0 0 1 7 7c0 3.8-3.6 7.8-7 11Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10.5" r="2.6" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

// GPS-arrow glyph (img/svg/navigation_icon.svg inlined with currentColor, per V2 convention).
const GpsArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.255668 8.08858C0.53296 8.36587 0.810252 8.45311 1.3574 8.45311L7.35706 8.4849C7.41252 8.4849 7.45986 8.4849 7.48387 8.51635C7.50754 8.54002 7.51532 8.5877 7.51532 8.63538L7.53899 14.643C7.5471 15.1898 7.63435 15.4671 7.91164 15.7443C8.2843 16.1248 8.80743 16.0615 9.19564 15.6811C9.40192 15.4748 9.56829 15.1343 9.71878 14.8171L15.782 1.74046C16.0989 1.07498 16.0593 0.583306 15.7347 0.258342C15.4171 -0.0585063 14.9258 -0.09807 14.2603 0.218778L1.18291 6.28184C0.865711 6.43232 0.525182 6.59869 0.318904 6.80496C-0.061528 7.19316 -0.124764 7.7085 0.255668 8.08892"
      fill="currentColor"
    />
  </svg>
);

function AddressSelect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Only set when the caller can't be safely returned to (see handleSelect).
  const returnTo = useLocation().state?.returnTo;
  const partner = useSelector(selectPartner);
  const mainUserData = useSelector((state) => state.userSlice.mainUserData);

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | results | empty | error
  // The live GPS address, shown as the pinned row's subtitle. Resolved eagerly on
  // mount only when permission is already granted — never the stored clientAddress,
  // which may be a manually picked address somewhere else.
  const [currentLocation, setCurrentLocation] = useState(null);
  // Post-tap failure flag: covers non-permission failures and environments where
  // the permissions API can't tell us anything up front.
  const [gpsDenied, setGpsDenied] = useState(false);
  // Known-denial state: the note shows on entry, not only after a failed tap.
  // Evidence-based via hasKnownGpsDenial — a live "denied" reading, or a denial
  // witnessed by a previous real attempt (home's entry refresh, a past tap):
  // inside the partner apps' WebView the permission API reads "prompt" even for
  // a denied user, so the live state alone can't know in advance. Clears itself
  // the moment a grant is observed (a "granted" reading, or a successful fix).
  const [permissionDenied, setPermissionDenied] = useState(false);

  // One Places billing session = all autocomplete calls + the final details call.
  const sessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(
    () => () => {
      clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    },
    []
  );

  // Re-evaluated on every permission change; the initial call also covers
  // environments where the permissions API is missing and the watcher never fires.
  useEffect(() => {
    if (!GPS_FLOW_ENABLED) return undefined;
    let cancelled = false;
    const apply = () =>
      hasKnownGpsDenial().then((known) => {
        if (!cancelled) setPermissionDenied(known);
      });
    apply();
    const unwatch = watchGeolocationPermission(apply);
    return () => {
      cancelled = true;
      unwatch();
    };
  }, []);

  // Eagerly resolve the live GPS address for display — silent, no spinner, and
  // never a first-time permission dialog: resolveLocationIfGranted attempts only
  // when permission is provably granted (or reads "unknown" with a past fix on
  // record) and skips otherwise, so this render can never raise a dialog or a
  // host-app settings jump.
  useEffect(() => {
    if (!GPS_FLOW_ENABLED) return undefined;
    let cancelled = false;
    (async () => {
      const result = await resolveLocationIfGranted();
      if (cancelled || !result.coords) return;
      try {
        const geocoded = await placesAPI.reverseGeocode(result.coords);
        if (cancelled) return;
        setCurrentLocation({
          ...geocoded,
          ...result.coords,
          formatted: formatAddress(geocoded.street, geocoded.houseNumber, geocoded.city),
        });
      } catch (err) {
        console.error("Could not resolve an address for the current location:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchSuggestions = async (input) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const requestId = ++requestIdRef.current;

    try {
      const results = await placesAPI.autocomplete(input, sessionTokenRef.current, controller.signal);
      if (requestId !== requestIdRef.current) return;
      setSuggestions(results);
      setStatus(results.length ? "results" : "empty");
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("Address autocomplete failed:", err);
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      setStatus("error");
    }
  };

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setGpsDenied(false);
    clearTimeout(debounceTimerRef.current);

    if (value.trim().length < MIN_QUERY_LENGTH) {
      abortControllerRef.current?.abort();
      requestIdRef.current++;
      setSuggestions([]);
      setStatus("idle");
      return;
    }

    sessionTokenRef.current ??= crypto.randomUUID();
    setStatus("loading");
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(value.trim()), DEBOUNCE_MS);
  };

  const handleSelect = async (suggestion) => {
    try {
      dispatch(isLoadChanger(true));

      const details = await placesAPI.getPlaceDetails(suggestion.placeId, sessionTokenRef.current);
      sessionTokenRef.current = null;

      // Backend first (optimistic: false): this screen must not claim an address
      // the DB refused — a failure lands in the catch below and nothing is stored.
      await commitClientLocation({ address: details, source: "address", mainUserData, dispatch });

      dispatch(isLoadChanger(false));

      // Callers that must not be returned to — the location gate, which would put
      // the user straight back on the screen they just answered — pass an explicit
      // destination. `replace` so the gate drops out of history with it.
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else {
        navigate(-1);
      }
    } catch (err) {
      dispatch(isLoadChanger(false));
      swalDialog(err);
    }
  };

  // The pinned "current location" row. Deliberately not commitGpsFix: its 100m
  // same-place guard would silently skip the write when the stored address is
  // nearby, but here the user explicitly asked to switch back to GPS (the stored
  // source must flip to "gps" either way).
  const handleSelectCurrentLocation = async () => {
    setGpsDenied(false);
    dispatch(isLoadChanger(true));
    try {
      // Commit exactly what the row displays when we have it; otherwise resolve
      // now — this is the tap-initiated path that may raise the permission dialog
      // (or, in the partner apps' WebView while denied, the device settings).
      let address = currentLocation;
      if (!address) {
        const result = await requestLocation();
        if (!result.coords) {
          dispatch(isLoadChanger(false));
          setGpsDenied(true);
          return;
        }
        const geocoded = await placesAPI.reverseGeocode(result.coords);
        address = { ...geocoded, ...result.coords };
      }

      await commitClientLocation({ address, source: "gps", mainUserData, dispatch });

      dispatch(isLoadChanger(false));
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else {
        navigate(-1);
      }
    } catch (err) {
      dispatch(isLoadChanger(false));
      swalDialog(err);
    }
  };

  // Direct URL hit by a partner outside the rollout → home.
  if (!isV2Enabled(V2_SCREEN_KEYS.ADDRESS_SELECT, partner)) return <Navigate to="/" replace />;

  return (
    <div className="addr-v2">
      <ScreenHeader title={t("ADDRESS_SELECT_TITLE")} onBack={() => navigate(-1)} variant="close" />

      <div className="addr-v2-search-wrap">
        <div className="addr-v2-search-pill">
          <span className="addr-v2-pill-icon">
            <PinIcon />
          </span>
          <input
            type="text"
            className="addr-v2-search-input"
            placeholder={t("ADDRESS_SEARCH_PLACEHOLDER")}
            value={query}
            onChange={handleQueryChange}
            autoFocus
          />
        </div>
      </div>

      <div className="addr-v2-list">
        {status === "idle" && GPS_FLOW_ENABLED && (
          <>
            <button type="button" className="addr-v2-row" onClick={handleSelectCurrentLocation}>
              <span className="addr-v2-row-icon">
                <GpsArrowIcon />
              </span>
              <span className="addr-v2-row-texts">
                <span className="addr-v2-row-main">{t("ADDRESS_CURRENT_LOCATION")}</span>
                {currentLocation?.formatted && <span className="addr-v2-row-secondary">{currentLocation.formatted}</span>}
              </span>
            </button>
            {(gpsDenied || permissionDenied) && (
              <p className="addr-v2-gps-note">{t("ADDRESS_CURRENT_LOCATION_DENIED")}</p>
            )}
          </>
        )}
        {suggestions.map((suggestion) => (
          <button type="button" key={suggestion.placeId} className="addr-v2-row" onClick={() => handleSelect(suggestion)}>
            <span className="addr-v2-row-icon">
              <PinIcon />
            </span>
            <span className="addr-v2-row-texts">
              <span className="addr-v2-row-main">{suggestion.mainText}</span>
              <span className="addr-v2-row-secondary">{suggestion.secondaryText}</span>
            </span>
          </button>
        ))}
      </div>

      {(status === "empty" || status === "error") && <p className="addr-v2-empty">{t("ADDRESS_NO_RESULTS")}</p>}
    </div>
  );
}

export default AddressSelect;
